import { Modal } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import CancelIcon from "@mui/icons-material/Cancel";
import BaseButton from "@/components/common/baseButton";
import FormsFunDayModalContent from "../formsFunDayModalContent/formsFunDayModalContent";
import { FormResponse } from "@/models/forms";
import { formatAmount, formatDate } from "@/util";
import api from "@/services/api";
import popupService from "@/services/popupService";

interface IFormsFunDayViewProps {
    formData: FormResponse | null;
}

const FormsFunDayView: FC<IFormsFunDayViewProps> = (props: IFormsFunDayViewProps): JSX.Element => {
    const { t } = useTranslation();
    const [openFunDayModal, setOpenFunDayModal] = useState(false);

    function handleFormYesOrNoProp(property: boolean | undefined) {
        if (property) {
            return t("forms.Yes");
        } else {
            return t("forms.No");
        }
    }

    function getRegionsInfo(
        regionOne: string | undefined,
        regionTwo: string | undefined,
        regionThree: string | undefined
    ): string {
        const regionDataArray = [regionOne, regionTwo, regionThree];
        const regionsArray = regionDataArray.filter((region) => region !== "").join(", ");

        return regionsArray;
    }

    function getAlternativeHolidayDatesInfo(dateOne: string, dateTwo: string, dateThree: string): string {
        var datesArray = [dateOne, dateTwo, dateThree];
        var filteredDates = datesArray
            .filter((date) => date !== "")
            .map((date) => formatDate(date))
            .join(", ");

        return filteredDates;
    }

    function getFunDayEnumsData(enumTypes: string): string {
        const typesArray = enumTypes
            .split(", ")
            .map((type) => t(`forms.CreateFunDay.${type}`))
            .join(", ");

        return typesArray;
    }

    const downloadPdf = async (formId: number) => {
        try {
            const response = await api.downloadFormPdf(formId);
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "FunDayForm.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            popupService.showApiError(error);
            return;
        }
    };

    const funDayModal = openFunDayModal ? (
        <div>
            <Modal
                open={openFunDayModal}
                onClose={() => setOpenFunDayModal(false)}
                sx={{ backdropFilter: "blur(6px)" }}
            >
                <div className="fun-day-modal-style">
                    <div className="fun-day-modal-header">
                        <div>{t("forms.FunDay.Header")}</div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <CancelIcon
                                onClick={() => setOpenFunDayModal(false)}
                                style={{ color: "#FFFFFF", cursor: "pointer" }}
                            />
                        </div>
                    </div>
                    <div className="fun-day-modal-content">
                        <FormsFunDayModalContent formData={props.formData} />
                        <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
                            <BaseButton
                                style={{ padding: "0px 110px" }}
                                type="button"
                                onClick={() => setOpenFunDayModal(false)}
                                className="cancel-button"
                            >
                                {t("button.cancel")}
                            </BaseButton>
                            <BaseButton
                                style={{ padding: "0px 90px" }}
                                type="submit"
                                className="basic-button"
                                onClick={() => downloadPdf(props.formData?.id ?? 0)}
                            >
                                {t("forms.DownloadPdf")}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    ) : (
        ""
    );

    return (
        <div className="forms-view-container">
            <div className="forms-view-header">
                <div style={{ display: "flex", alignItems: "center" }} className="font-style-white-bold">
                    {t("forms.FunDay.Header")}
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "9px",
                    }}
                    className="font-style-white-bold"
                >
                    <i style={{ paddingTop: 2 }} className="icon-f icon-ViewFormIcon fs-18" />
                    <span
                        style={{
                            cursor: "pointer",
                            textDecorationLine: "underline",
                        }}
                        onClick={() => setOpenFunDayModal(true)}
                    >
                        {t("forms.ViewForm")}
                    </span>
                </div>
            </div>
            <div className="forms-view-content">
                {funDayModal}
                <div style={{ marginBottom: "15px" }} className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.ClientName")}</div>
                            <div className="font-style-bold-16">{props.formData?.clientName}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Mobile")}</div>
                            <div className="font-style-bold-16">{props.formData?.mobile}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Email")}</div>
                            <div className="font-style-bold-16">{props.formData?.email}</div>
                        </div>
                    </div>
                </div>
                <div style={{ marginBottom: "15px" }} className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.FunDayGoal")}</div>
                            <div className="font-style-bold-16">{props.formData?.formGoal}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.ParticipantsNumber")}</div>
                            <div className="font-style-bold-16">
                                {props.formData?.numberOfParticipants?.toLocaleString("en-US")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.DesiredRegions")}</div>
                            <div className="font-style-bold-16">
                                {getRegionsInfo(
                                    props.formData?.desiredRegionOne ?? "",
                                    props.formData?.desiredRegionTwo ?? "",
                                    props.formData?.desiredRegionThree ?? ""
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ marginBottom: "15px" }} className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.BudgetPerEmployee")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.budgetPerEmployee, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.FamilyBudget")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.familyBudget, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.BudgetForCouple")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.budgetForCouple, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.budgetComments}</div>
                        </div>
                    </div>
                </div>
                <div style={{ marginBottom: "15px" }} className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.RequestedFrom")}</div>
                            <div className="font-style-bold-16">{props.formData?.requestedFrom}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.RequestedTo")}</div>
                            <div className="font-style-bold-16">{props.formData?.requestedTo}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.AlternativeHolidayDates")}</div>
                            <div className="font-style-bold-16">
                                {getAlternativeHolidayDatesInfo(
                                    props.formData?.alternativeHolidayDateOne ?? "",
                                    props.formData?.alternativeHolidayDateTwo ?? "",
                                    props.formData?.alternativeHolidayDateThree ?? ""
                                )}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.alternativeHolidayDateComments}</div>
                        </div>
                    </div>
                </div>
                <div style={{ marginBottom: "15px" }} className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Visitors")}</div>
                            <div className="font-style-bold-16">
                                {t(`forms.CreateFunDay.${props.formData?.visitors}`)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.NeedBus")}</div>
                            <div className="font-style-bold-16">{handleFormYesOrNoProp(props.formData?.needBus)}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.NeedGuide")}</div>
                            <div className="font-style-bold-16">{handleFormYesOrNoProp(props.formData?.needGuide)}</div>
                        </div>
                    </div>
                </div>
                <div style={{ marginBottom: "15px" }} className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Nature")}</div>
                            <div className="font-style-bold-16">
                                {getFunDayEnumsData(props.formData?.natureFunDayType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.natureFunDayComments}</div>
                        </div>
                    </div>
                </div>
                <div style={{ marginBottom: "15px" }} className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.InterestedIn")}</div>
                            <div className="font-style-bold-16">
                                {getFunDayEnumsData(props.formData?.mealType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.mealComments}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div style={{ padding: "13px 10px 14px 15px" }}>
                            <div className="font-style">{t("forms.FunDay.WantToIncorporate")}</div>
                            <div style={{ paddingTop: 7 }} className="font-style-bold-16">
                                {handleFormYesOrNoProp(props.formData?.wantIncorporateLectureRecognition)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Budget")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(
                                    props.formData?.incorporateLectureRecognitionBudget ?? 0,
                                    props.formData?.currencyId
                                )}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">
                                {props.formData?.incorporateLectureRecognitionComments}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormsFunDayView;
